import {
  AdminSpinner,
  Button,
  connectLocationWithProvider,
  connectProvider,
  Div,
  ErrorToast,
  H3,
  Img,
  lightTheme,
  Modal,
  P,
  Provider,
  ProviderLogoWrapper,
  renderProviderImage,
  renderProviderName,
  SimpleBrandEntity,
  SimpleLocationEntity,
  SyncStatus,
  reportError,
} from '@vgrubs/components';
import { useProviderSyncStatus } from '../../store/useProviderSyncStatus';
import { useLocationBrandPicker } from 'components/src/store/useLocationBrandPicker';
import { useState } from 'react';
import { useSafeWindowOpen } from 'components/src/hooks/useSafeWindowOpen';
import { useIsIOSSafari } from 'components/src/hooks/useIsIOSSafari';
import { isAxiosError } from 'axios';

export const SetupMenuToContinueModal = ({
  syncStatus,
  isOpen,
  closeModal,
  locationId,
  brandId,
  provider,
  haveMenuSet,
  haveWorkingHoursSet,
  location,
  brand,
  pathPrefix,
  id,
  buttonLoading,
}: {
  syncStatus: SyncStatus | null;
  isOpen: boolean;
  closeModal: () => void;
  locationId: string | number;
  brandId: string | number;
  provider: Provider;
  haveMenuSet: boolean | null | undefined;
  haveWorkingHoursSet: boolean | null | undefined;
  location: SimpleLocationEntity | null;
  brand: SimpleBrandEntity | null;
  pathPrefix: 'locations' | 'brands';
  id: string | number;
  buttonLoading: boolean;
}) => {
  const [loading, setLoading] = useState(false);

  const { setInitializedProviderConnection } = useProviderSyncStatus();
  const { setSelectedLocation, setSelectedBrand } = useLocationBrandPicker(
    (state) => state
  );

  const connectFunction =
    pathPrefix === 'locations' ? connectLocationWithProvider : connectProvider;

  const openSafe = useSafeWindowOpen();
  const isIOSSafari = useIsIOSSafari();

  const [url, setUrl] = useState<string | null>(null);

  const handleRedirect = (url: string) => {
    openSafe(url, {
      immediate: true,
      target: '_blank',
    });

    closeModal?.();
    setUrl(null);
  };

  async function handleContinueConnection() {
    try {
      setLoading(true);
      const res = await connectFunction(id, {
        provider,
        storeId: null,
        shouldUseFoodhub: true,
      });

      setLoading(false);
      const url = res.data.data;

      if (isIOSSafari) {
        setUrl(url);
      } else {
        handleRedirect(url);
      }
    } catch (error) {
      setLoading(false);

      if (isAxiosError(error)) {
        if (
          error.response?.data.message ===
          'You cannot connect this provider without a menu first.'
        ) {
          ErrorToast(
            'Wait a moment while menu is being published... Try again shortly.'
          );
        } else {
          reportError(error);
        }
      }
    } finally {
      setLoading(false);
    }
  }

  return (
    <>
      {loading && <AdminSpinner />}
      <Modal isOpen={isOpen} closeModal={closeModal} logo={false}>
        <Div $flex $center>
          <ProviderLogoWrapper>
            <span>
              <Img alt="" src={renderProviderImage(provider)} />
            </span>
          </ProviderLogoWrapper>
        </Div>
        <H3 $mb="8">Setup Working Hours then Menu to Continue</H3>
        <P $mb="24">
          In order to continue with provider connection, please make sure the
          working hours are set correctly then publish the menu to{' '}
          {renderProviderName(provider)}.{' '}
        </P>

        <Button
          $w="100%"
          onClick={() => {
            setSelectedLocation(location);
            setSelectedBrand(brand);
            window.open(`/working-hours`, '_blank');
            setInitializedProviderConnection(provider);
          }}
          $mb="8"
          $success={!!haveWorkingHoursSet}
          $selected={!!haveWorkingHoursSet}
          $loading={buttonLoading && !haveWorkingHoursSet}
          $loadingColor={lightTheme.primary}
        >
          {haveWorkingHoursSet
            ? 'Working Hours Completed'
            : 'Manage Working Hours'}
        </Button>

        <Button
          $w="100%"
          onClick={() => {
            if (!haveWorkingHoursSet) {
              ErrorToast('Please set working hours first.');
              return;
            }
            window.open(
              `/menu/${locationId}/location/${brandId}/brand`,
              '_blank'
            );
            setInitializedProviderConnection(provider);
          }}
          $success={
            syncStatus === SyncStatus.Completed ||
            syncStatus === SyncStatus.Waiting
          }
          $selected={
            syncStatus === SyncStatus.Completed ||
            syncStatus === SyncStatus.Waiting
          }
          $mb="8"
          $loading={syncStatus === SyncStatus.InProgress}
          $loadingColor={lightTheme.primary}
        >
          {haveMenuSet ? 'Menu Completed' : 'Manage Menu'}
        </Button>

        {!url && (
          <Button
            $w="100%"
            onClick={handleContinueConnection}
            disabled={!haveMenuSet || !haveWorkingHoursSet}
          >
            Continue
          </Button>
        )}

        {url && (
          <Button
            $selected
            $w="100%"
            $mt="16"
            onClick={() => handleRedirect(url)}
          >
            Proceed to authorization page
          </Button>
        )}
      </Modal>
    </>
  );
};
